/* Ad spend by app × channel: 7d and 30d spend vs the period before, platform-reported trials/purchases and cost per
   purchase. The lead totals by channel and by app. Channels a product should run but that aren't synced come from
   dataCoverage (one list of expected feeds) and fold away as "not synced". API: marketingAnalytics.spendWindows. */
import { ECoverageKind, ECoverageState } from "@aix/aixinternal-shared/enums"
import type { inferRouterOutputs } from "@trpc/server"
import { useCallback } from "react"

import type { AppRouter } from "../../../../../aixinternal-api/src/router/_app"
import { trpc } from "../../../lib/trpc"
import { worstVerdict } from "../homeDashboardModel"
import { EVerdict } from "../homeDashboard.types"
import type { IWidgetDefinition, IWidgetResult, IWidgetRow } from "../homeDashboard.types"
import { KeyValues } from "./WidgetParts"
import { WIDGET_DAILY_QUERY_OPTIONS, useQueryWidget } from "./widgetQuery"

type TOutputs = inferRouterOutputs<AppRouter>
type TSpend = TOutputs["marketingAnalytics"]["spendWindows"]
type TChannel = TSpend["channels"][number]
type TFeed = TOutputs["dataCoverage"]["get"]["feeds"][number]

const DAY_MS = 86_400_000
const MINOR_PER_MAJOR = 100
/** Platforms report yesterday at the latest; older than this means the sync stopped */
const STALE_DAYS = 2
/** Week-over-week spend jump worth a look */
const SPEND_JUMP = 0.5

const CHANNEL: Record<string, string> = { meta: "Meta", google_ads: "Google", tiktok_ads: "TikTok" }
const APP: Record<string, string> = {
  vidmax: "VidMax",
  lunchbreak: "Lunchbreak",
  pebble: "Pebble",
  lumi: "Lumi",
  recoverme: "RecoverMe",
  "saas-engine": "SaaS Engine",
}
const channelName = (provider: string) => CHANNEL[provider] ?? provider
const appName = (key: string) => APP[key] ?? key

const money = (minor: number, currency = "USD") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(minor / MINOR_PER_MAJOR)

const change = (current: number, previous: number): number | null =>
  previous > 0 ? (current - previous) / previous : null

const pct = (ratio: number | null) => {
  if (ratio === null) return "new"
  const rounded = Math.round(ratio * 100)
  return `${rounded > 0 ? "+" : ""}${rounded}%`
}

const channelState = (c: TChannel, now: number): { verdict: EVerdict; pill: string } => {
  const latest = c.latestDate ? Date.parse(`${c.latestDate}T00:00:00Z`) : 0
  if (now - latest > (STALE_DAYS + 1) * DAY_MS) return { verdict: EVerdict.WATCH, pill: "stale" }
  if (c.spend7 === 0 && c.spendPrev7 > 0) return { verdict: EVerdict.WATCH, pill: "stopped" }
  const weekly = change(c.spend7, c.spendPrev7)
  if (weekly !== null && weekly >= SPEND_JUMP) return { verdict: EVerdict.WATCH, pill: pct(weekly) }
  return { verdict: EVerdict.CLEAR, pill: pct(weekly) }
}

const channelRow = (c: TChannel, now: number): IWidgetRow => {
  const { verdict, pill } = channelState(c, now)
  const cpp = c.purchases30 > 0 ? money(c.spend30 / c.purchases30, c.currency) : "—"
  const cpt = c.trials30 > 0 ? money(c.spend30 / c.trials30, c.currency) : "—"
  return {
    key: `${c.productKey}:${c.provider}:${c.currency}`,
    company: c.productKey,
    verdict,
    pill: { text: pill, verdict },
    title: `${appName(c.productKey)} · ${channelName(c.provider)}`,
    sub: `${money(c.spend7, c.currency)} 7d (${pct(change(c.spend7, c.spendPrev7))}) · ${money(c.spend30, c.currency)} 30d (${pct(change(c.spend30, c.spendPrev30))})`,
    meta: `${cpp}/purchase`,
    details: (
      <KeyValues
        pairs={[
          ["Spend 7d", `${money(c.spend7, c.currency)} vs ${money(c.spendPrev7, c.currency)} the week before`],
          ["Spend 30d", `${money(c.spend30, c.currency)} vs ${money(c.spendPrev30, c.currency)} the 30d before`],
          [
            "Platform-reported 30d",
            `${c.trials30.toLocaleString()} trials (${cpt} each) · ${c.purchases30.toLocaleString()} purchases (${cpp} each)`,
          ],
          [
            "Delivery 30d",
            `${c.impressions30.toLocaleString()} impressions · ${c.clicks30.toLocaleString()} clicks${c.impressions30 ? ` · ${((c.clicks30 / c.impressions30) * 100).toFixed(2)}% CTR` : ""}`,
          ],
          ["Data through", c.latestDate],
        ]}
      />
    ),
  }
}

const missingRow = (feed: TFeed): IWidgetRow => ({
  key: feed.id,
  company: feed.company ?? undefined,
  verdict: EVerdict.UNKNOWN,
  pill: { text: "not synced", verdict: EVerdict.UNKNOWN },
  title: `${appName(feed.company ?? "")} · ${channelName(feed.provider)}`,
  sub: "Spend isn't ingested; set up a daily sync (kit-marketing-analytics)",
  quiet: true,
})

/** "Meta $12.6k · Google $1k" for one grouping over 30 days (USD only, so totals add up) */
const totalsBy = (channels: TChannel[], keyOf: (c: TChannel) => string) => {
  const sums = new Map<string, number>()
  for (const c of channels) if (c.currency === "USD") sums.set(keyOf(c), (sums.get(keyOf(c)) ?? 0) + c.spend30)
  return [...sums.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([name, minor]) => `${name} ${money(minor)}`)
    .join(" · ")
}

const renderSpend = (data: TSpend, feeds: TFeed[] | undefined): IWidgetResult => {
  const now = Date.now()
  const live = data.channels.map((c) => channelRow(c, now))
  const missing = (feeds ?? [])
    .filter((f) => f.kind === ECoverageKind.AD_SPEND && f.state === ECoverageState.MISSING)
    .map(missingRow)
  const usd = data.channels.filter((c) => c.currency === "USD")
  const spend7 = usd.reduce((sum, c) => sum + c.spend7, 0)
  const prev7 = usd.reduce((sum, c) => sum + c.spendPrev7, 0)
  const spend30 = usd.reduce((sum, c) => sum + c.spend30, 0)

  return {
    verdict: worstVerdict(live.map((r) => r.verdict)),
    count: `${live.length} channel${live.length === 1 ? "" : "s"} synced`,
    lead: data.channels.length ? (
      <KeyValues
        pairs={[
          ["Total", `${money(spend7)} 7d (${pct(change(spend7, prev7))}) · ${money(spend30)} 30d`],
          ["By channel 30d", totalsBy(data.channels, (c) => channelName(c.provider))],
          ["By app 30d", totalsBy(data.channels, (c) => appName(c.productKey))],
        ]}
      />
    ) : undefined,
    rows: [...live, ...missing],
    quietLabel: `${missing.length} channels not synced`,
    empty: "No ad spend synced yet.",
    foot: "Trials and purchases are what the ad platform reports, not our attribution.",
  }
}

export const adSpendWidget: IWidgetDefinition = {
  id: "ad-spend",
  title: "Ad spend",
  description: "Spend by app and channel, 7d and 30d vs prior, with cost per purchase",
  columns: { 2: 1, 3: 2, 4: 2, 5: 3, 6: 3 },
  useWidget: () => {
    const coverage = trpc.dataCoverage.get.useQuery(undefined, WIDGET_DAILY_QUERY_OPTIONS)
    const render = useCallback((data: TSpend) => renderSpend(data, coverage.data?.feeds), [coverage.data])
    return useQueryWidget(trpc.marketingAnalytics.spendWindows.useQuery(undefined, WIDGET_DAILY_QUERY_OPTIONS), render)
  },
}
